import { useState, useEffect, useCallback } from 'react';
import { municipalitiesService, Municipality, CreateMunicipalityData, UpdateMunicipalityData } from '@/services/municipalities';

export const useMunicipalities = () => {
  console.log('🔧 useMunicipalities: Hook initialized');
  
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMunicipalities = useCallback(async () => {
    console.log('🔧 useMunicipalities: fetchMunicipalities called');
    try {
      setLoading(true);
      setError(null);
      console.log('🔧 useMunicipalities: About to call municipalitiesService.getMunicipalities()');
      const data = await municipalitiesService.getMunicipalities();
      console.log('🔧 useMunicipalities: Successfully fetched municipalities:', data);
      setMunicipalities(data);
    } catch (err: unknown) {
      console.error('🔧 useMunicipalities: Error fetching municipalities:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch municipalities';
      setError(errorMessage);
      // Don't throw the error, just set it in state
    } finally {
      setLoading(false);
    }
  }, []);

  const createMunicipality = useCallback(async (data: CreateMunicipalityData) => {
    console.log('🔧 useMunicipalities: createMunicipality called with:', data);
    try {
      setError(null);
      const newMunicipality = await municipalitiesService.createMunicipality(data);
      setMunicipalities(prev => [newMunicipality, ...prev]);
      return newMunicipality;
    } catch (err: unknown) {
      console.error('🔧 useMunicipalities: Error creating municipality:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create municipality';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const updateMunicipality = useCallback(async (id: string, data: UpdateMunicipalityData) => {
    console.log('🔧 useMunicipalities: updateMunicipality called with:', { id, data });
    try {
      setError(null);
      await municipalitiesService.updateMunicipality(id, data);
      setMunicipalities(prev => 
        prev.map(m => m.id === id ? { ...m, ...data, updatedAt: new Date().toISOString() } : m)
      );
    } catch (err: unknown) {
      console.error('🔧 useMunicipalities: Error updating municipality:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to update municipality';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const deleteMunicipality = useCallback(async (id: string) => {
    console.log('🔧 useMunicipalities: deleteMunicipality called with:', id);
    try {
      setError(null);
      await municipalitiesService.deleteMunicipality(id);
      setMunicipalities(prev => prev.filter(m => m.id !== id));
    } catch (err: unknown) {
      console.error('🔧 useMunicipalities: Error deleting municipality:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete municipality';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const sendInvitation = useCallback(async (id: string) => {
    console.log('🔧 useMunicipalities: sendInvitation called with:', id);
    try {
      setError(null);
      await municipalitiesService.sendInvitation(id);
      // Update the municipality with invitation data
      setMunicipalities(prev => 
        prev.map(m => m.id === id ? { 
          ...m, 
          invitationSentAt: new Date().toISOString(),
          status: 'trial' as const
        } : m)
      );
    } catch (err: unknown) {
      console.error('🔧 useMunicipalities: Error sending invitation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to send invitation';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getMunicipalityById = useCallback(async (id: string) => {
    console.log('🔧 useMunicipalities: getMunicipalityById called with:', id);
    try {
      setError(null);
      return await municipalitiesService.getMunicipalityById(id);
    } catch (err: unknown) {
      console.error('🔧 useMunicipalities: Error getting municipality by id:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch municipality';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getMunicipalitiesByStatus = useCallback(async (status: Municipality['status']) => {
    console.log('🔧 useMunicipalities: getMunicipalitiesByStatus called with:', status);
    try {
      setLoading(true);
      setError(null);
      const data = await municipalitiesService.getMunicipalitiesByStatus(status);
      setMunicipalities(data);
    } catch (err: unknown) {
      console.error('🔧 useMunicipalities: Error fetching municipalities by status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch municipalities by status';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('🔧 useMunicipalities: useEffect triggered, calling fetchMunicipalities');
    fetchMunicipalities();
  }, [fetchMunicipalities]);

  console.log('🔧 useMunicipalities: Returning hook state:', { municipalities, loading, error });

  return {
    municipalities,
    loading,
    error,
    fetchMunicipalities,
    createMunicipality,
    updateMunicipality,
    deleteMunicipality,
    sendInvitation,
    getMunicipalityById,
    getMunicipalitiesByStatus,
  };
}; 